import { X, Car, User, Shield } from 'lucide-react'
import { formatDate } from '@/lib/formatters'

import { VeiculoComAssociado } from '@/services/veiculo.service'

interface Props {
    veiculo: VeiculoComAssociado | null
    onClose: () => void
}

export function VeiculoDetailDrawer({ veiculo, onClose }: Props) {
    if (!veiculo) return null
    const v = veiculo

    return (
        <div className="fixed inset-0 z-50 flex justify-end animate-fade-in">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-md bg-white shadow-2xl h-full overflow-y-auto animate-slide-in-right flex flex-col">
                <div className="sticky top-0 bg-white border-b border-surface-100 p-4 flex items-center justify-between z-10">
                    <h3 className="text-lg font-bold text-surface-900">Detalhes do Veículo</h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-700 transition-colors">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-6 space-y-8 flex-1">
                    {/* Placa e Modelo Principal */}
                    <div className="flex items-center gap-5">
                        <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center text-primary-600">
                            <Car size={32} />
                        </div>
                        <div>
                            <p className="text-3xl font-mono font-bold text-surface-900 tracking-tighter uppercase">{v.placa}</p>
                            <p className="text-sm font-bold text-surface-700/40 uppercase tracking-widest">{v.marca} — {v.modelo}</p>
                        </div>
                    </div>

                    {/* Especificações */}
                    <div className="grid grid-cols-2 gap-4">
                        <SpecBox label="Ano" value={v.ano || '—'} />
                        <SpecBox label="Cor" value={v.cor || '—'} />
                        <div className="col-span-2">
                            <SpecBox label="Chassi" value={v.chassi || 'Não informado'} mono />
                        </div>
                    </div>

                    {/* Associado Responsável */}
                    <div className="pt-6 border-t border-surface-100">
                        <p className="text-[10px] font-bold text-surface-700/40 uppercase tracking-widest mb-4">Associado Responsável</p>
                        <div className="flex items-center gap-4 p-4 rounded-2xl bg-surface-50/50 border border-surface-100">
                            <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-primary-600">
                                <User size={20} />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-surface-900">{v.associado_nome || 'Associado'}</p>
                                <p className="text-xs text-primary-600 font-medium">Ver perfil completo</p>
                            </div>
                        </div>
                    </div>

                    {/* Status de Proteção (Placeholder para o futuro) */}
                    <div className="p-5 rounded-3xl bg-green-50 border border-green-100 flex items-center gap-4">
                        <div className="p-2 rounded-xl bg-green-600 text-white shadow-lg shadow-green-200">
                            <Shield size={20} />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-green-700">Proteção Ativa</p>
                            <p className="text-xs text-green-600/70">Contrato vinculado e em dia.</p>
                        </div>
                    </div>

                    {/* Datas */}
                    <div className="flex justify-between items-center text-[10px] text-surface-700/40 font-bold uppercase tracking-widest pt-6 border-t border-surface-100">
                        <span>Cadastrado em</span>
                        <span className="text-surface-700/60">{formatDate(v.created_at)}</span>
                    </div>
                </div>

                <div className="sticky bottom-0 bg-white p-4 border-t border-surface-100 flex gap-3">
                    <button onClick={onClose} className="btn btn-secondary flex-1">Fechar</button>
                    <button className="btn btn-primary flex-1">Editar Veículo</button>
                </div>
            </div>
        </div>
    )
}

function SpecBox({ label, value, mono }: { label: string; value: string | number; mono?: boolean }) {
    return (
        <div className="p-4 rounded-2xl bg-white border border-surface-100 shadow-sm">
            <p className="text-[10px] font-bold text-surface-700/40 uppercase tracking-widest mb-1">{label}</p>
            <p className={`text-sm font-bold text-surface-900 ${mono ? 'font-mono' : ''}`}>{value}</p>
        </div>
    )
}
